import type { FC } from "react";

import _ from "lodash";
import styled from "styled-components";

import { SWReactCountryIcons, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { Label } from "app/pages/industry-analysis/home/enriched-custom-industries/commonStyled";
import { hasEnrichedCustomIndustryPermission } from "app/pages/industry-analysis/home/enriched-custom-industries/hasEnrichedCustomIndustryPermission";
import type { IEnrichedIndustry } from "app/pages/industry-analysis/home/enriched-custom-industries/types";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { getLinesOfBusinessSegments, getCustomSegments } from "app/services/segments/LinesOfBusinessUtils";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const { carbon } = colorsPalettes;

interface IHeader {
    industry: IEnrichedIndustry;
}

const Container = styled(FlexColumn)`
    width: 100%;
    gap: 4px;
`;

const ContentContainer = styled(FlexRow)`
    align-items: center;
    gap: 4px;
`;

const Title = styled.span`
    ${setFontVariant("h6", carbon[500])};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

export const Header: FC<IHeader> = (props) => {
    const { industry } = props;
    const { text, domains = [], segments = [] } = industry;
    const country = CategoryServiceUtils.getIndustryCountry(industry);
    const hasDomains = domains.length > 0;

    // LOBs are stored in segments array as a (hopefully temporary) compromise, but they're not actual segments
    const lobIds = getLinesOfBusinessSegments(segments);
    const actualSegments = getCustomSegments(segments);
    const hasLobs = lobIds.length > 0;
    const hasActualSegments = actualSegments.length > 0;

    return (
        <Container>
            <Title>{text}</Title>
            <FlexRow gap={"10px"}>
                {hasDomains ? (
                    <ContentContainer>
                        <SWReactIcons
                            iconName={"globe"}
                            size={"xs"}
                        />
                        <Label>{domains.length}</Label>
                        <Label>{translate(domains.length > 1 ? "common.websites" : "common.website")}</Label>
                    </ContentContainer>
                ) : null}
                {hasActualSegments ? (
                    <ContentContainer>
                        <SWReactIcons
                            iconName={"chart-pie"}
                            size={"xs"}
                        />
                        <Label>{actualSegments.length}</Label>
                        <Label>
                            {_.startCase(translate(actualSegments.length > 1 ? "common.segments" : "common.segment"))}
                        </Label>
                    </ContentContainer>
                ) : null}
                {hasLobs ? (
                    <ContentContainer>
                        <SWReactIcons
                            iconName={"chart-pie"}
                            size={"xs"}
                        />
                        <Label>{lobIds.length}</Label>
                        <Label>{_.startCase(translate(lobIds.length > 1 ? "common.lobs" : "common.lob"))}</Label>
                    </ContentContainer>
                ) : null}
                <ContentContainer>
                    {hasEnrichedCustomIndustryPermission() ? (
                        <SWReactCountryIcons
                            countryCode={country.id}
                            size={"xs"}
                        />
                    ) : null}

                    <Label>{country.text}</Label>
                </ContentContainer>
            </FlexRow>
        </Container>
    );
};
